import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Site, SiteUser, SiteFilters } from 'types'

export interface SitesManagerState {
  error?: string
  message: string
  sites: {
    data?: Site[]
    isLoading: boolean
    criteria: Partial<SiteFilters>
  }
  siteUsers: {
    addBulkUsersResponse?: {
      newSiteUsers?: SiteUser[]
      failedSiteUsers?: SiteUser[]
      invalidSiteUsers?: SiteUser[]
      sendEmail?: boolean
    }
    data?: { [key: number]: SiteUser[] }
    isLoading: boolean
    isDeleting: boolean
    isUpdating: boolean
    isBulkOperationCompleted: boolean
  }
  remindUsers: {
    success?: boolean
    isLoading: boolean
  }
  mergeSiteState: {
    isMerging: boolean
    mergeSiteResults?: any
    mergeSiteError?: string
  }
  externalSiteMapping: {
    file?: File
    fileName?: string
    uploadResults?: any
    error?: string
  }
  isLoading: boolean
}

const productOrder = ['K8P', 'ADL', 'BPU', 'CPU']

const sortLicensesBySku = (licenseA, licenseB) => {
  const { skuCode: licenseASku } = licenseA.product
  const { skuCode: licenseBSku } = licenseB.product
  return productOrder.indexOf(licenseASku) - productOrder.indexOf(licenseBSku)
}

const sortListByName = (siteA, siteB) => siteA.name.localeCompare(siteB.name)

// Helper to ensure siteUsers.data exists and is initialized for a siteId
const ensureSiteUsersData = (state: SitesManagerState, siteId: number) => {
  if (!state.siteUsers.data) {
    state.siteUsers.data = {}
  }
  if (!state.siteUsers.data[siteId]) {
    state.siteUsers.data[siteId] = []
  }
}

export const initialState: SitesManagerState = {
  error: null,
  message: 'Loading',
  sites: {
    data: null,
    isLoading: false,
    criteria: {},
  },
  siteUsers: {
    addBulkUsersResponse: null,
    data: null,
    isLoading: false,
    isDeleting: false,
    isUpdating: false,
    isBulkOperationCompleted: false,
  },
  remindUsers: {
    success: null,
    isLoading: false,
  },
  mergeSiteState: {
    isMerging: false,
    mergeSiteResults: null,
    mergeSiteError: null,
  },
  externalSiteMapping: {
    file: null,
    fileName: null,
    uploadResults: null,
    error: null,
  },
  isLoading: false,
}

const slice = createSlice({
  name: 'SitesManager',
  initialState,
  reducers: {
    clearSites(state) {
      state.error = null
      state.message = 'Loading'
      state.sites = {
        data: null,
        isLoading: false,
        criteria: {},
      }
      state.siteUsers = {
        ...state.siteUsers,
        data: null,
        isLoading: false,
      }
    },
    fetchingSites(state) {
      state.sites.isLoading = true
      state.error = null
    },
    fetchingSitesSuccess(state, action: PayloadAction<Site[]>) {
      // Sort licenses by SKU for each site (if licenses exist)
      action.payload.forEach((site: any) => {
        site.licenses?.sort(sortLicensesBySku)
      })
      state.sites.data = action.payload.sort(sortListByName)
      state.sites.isLoading = false
      state.error = null
    },
    fetchingSitesError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.sites.isLoading = false
    },
    mergingSite(state) {
      state.mergeSiteState.isMerging = true
      state.mergeSiteState.mergeSiteError = null
    },
    mergingSiteSuccess(state, action: PayloadAction<any>) {
      state.mergeSiteState.isMerging = false
      state.mergeSiteState.mergeSiteResults = action.payload
      state.mergeSiteState.mergeSiteError = null
    },
    mergingSiteError(state, action: PayloadAction<string>) {
      state.mergeSiteState.isMerging = false
      state.mergeSiteState.mergeSiteError = action.payload
    },
    setSiteSearchCriteria(
      state,
      action: PayloadAction<SiteFilters | Partial<SiteFilters>>,
    ) {
      state.sites.criteria = action.payload as Partial<SiteFilters>
    },
    updatingSite(state) {
      state.isLoading = true
      state.error = null
    },
    updatingSiteSuccess(state, action: PayloadAction<Site>) {
      state.isLoading = false
      state.error = null
      // Update the site in the sites array if it exists
      const index = state.sites.data?.findIndex(s => s.id === action.payload.id)
      if (index !== undefined && index !== -1) {
        state.sites.data[index] = action.payload
      }
    },
    updatingSiteError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.isLoading = false
    },
    fetchingSiteUsers(state) {
      state.siteUsers.isLoading = true
      state.error = null
    },
    fetchingSiteUsersSuccess(
      state,
      action: PayloadAction<{ siteId: number; siteUsers: SiteUser[] }>,
    ) {
      if (!state.siteUsers.data) {
        state.siteUsers.data = {}
      }
      state.siteUsers.data[action.payload.siteId] = action.payload.siteUsers
      state.siteUsers.isLoading = false
      state.error = null
    },
    fetchingSiteUsersError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.siteUsers.isLoading = false
    },
    creatingSiteUser(state) {
      state.siteUsers.isUpdating = true
      state.error = null
    },
    creatingSiteUserSuccess(state, action: PayloadAction<SiteUser>) {
      ensureSiteUsersData(state, action.payload.siteId)
      state.siteUsers.data[action.payload.siteId].push(action.payload)
      state.siteUsers.isUpdating = false
      state.error = null
    },
    creatingSiteUserError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.siteUsers.isUpdating = false
    },
    creatingSiteUsers(state) {
      state.siteUsers.isLoading = true
      state.error = null
    },
    creatingSiteUsersSuccess(
      state,
      action: PayloadAction<{
        siteId: number
        newSiteUsers: SiteUser[]
        failedSiteUsers: SiteUser[]
        invalidSiteUsers: SiteUser[]
        sendEmail: boolean
      }>,
    ) {
      ensureSiteUsersData(state, action.payload.siteId)
      state.siteUsers.data[action.payload.siteId].push(
        ...action.payload.newSiteUsers,
      )
      state.siteUsers.addBulkUsersResponse = {
        newSiteUsers: action.payload.newSiteUsers,
        failedSiteUsers: action.payload.failedSiteUsers,
        invalidSiteUsers: action.payload.invalidSiteUsers,
        sendEmail: action.payload.sendEmail,
      }
      state.siteUsers.isLoading = false
      state.siteUsers.isBulkOperationCompleted = true
      state.error = null
    },
    creatingSiteUsersError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.siteUsers.isLoading = false
      state.siteUsers.isBulkOperationCompleted = true
    },
    remindingSiteUser(state) {
      state.remindUsers.isLoading = true
      state.error = null
    },
    remindingSiteUserSuccess(state) {
      state.remindUsers.success = true
      state.remindUsers.isLoading = false
      state.error = null
    },
    remindingSiteUserError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.remindUsers.isLoading = false
      state.remindUsers.success = false
    },
    remindingSiteUsers(state) {
      state.remindUsers.isLoading = true
      state.error = null
    },
    remindingSiteUsersSuccess(state) {
      state.remindUsers.success = true
      state.remindUsers.isLoading = false
      state.error = null
    },
    remindingSiteUsersError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.remindUsers.isLoading = false
      state.remindUsers.success = false
    },
    updatingSiteUser(state) {
      state.siteUsers.isUpdating = true
      state.error = null
    },
    updatingSiteUserSuccess(state, action: PayloadAction<SiteUser>) {
      const { siteId, id } = action.payload
      const siteUsers = state.siteUsers.data?.[siteId]
      if (siteUsers) {
        const siteUserIndex = siteUsers.findIndex(user => user.id === id)
        if (siteUserIndex !== -1) {
          siteUsers[siteUserIndex] = action.payload
        }
        // Filter out inactive users
        state.siteUsers.data[siteId] = siteUsers.filter(
          (u: any) => u.isActive === true,
        )
      }
      state.siteUsers.isUpdating = false
      state.error = null
    },
    updatingSiteUserError(state, action: PayloadAction<any>) {
      state.error = action.payload
      state.siteUsers.isUpdating = false
    },
    resetBulkAddUsers(state) {
      state.siteUsers.isBulkOperationCompleted = false
      state.siteUsers.addBulkUsersResponse = null
    },
    bulkAddingExternalSiteMapping(state) {
      state.isLoading = true
      state.error = null
    },
    bulkAddingExternalSiteMappingSuccess(state, action: PayloadAction<any>) {
      state.externalSiteMapping.uploadResults = action.payload
      state.isLoading = false
    },
    bulkAddingExternalSiteMappingError(state, action: PayloadAction<any>) {
      state.externalSiteMapping.error = action.payload
      state.isLoading = false
    },
  },
})

export const actions = slice.actions
export default slice.reducer
